<template>
  <v-dialog v-model="dialogVisible" max-width="800" scrollable persistent>
    <v-card v-if="customer">
      <v-card-title class="d-flex align-center py-2 px-3">
        <v-icon size="20" class="mr-2" color="primary">mdi-account-edit</v-icon>
        Edit Customer
        <v-chip
          v-if="customer.customer_type"
          size="x-small"
          :color="customer.customer_type === 'Commercial' ? 'primary' : 'success'"
          class="ml-2"
          variant="tonal"
        >
          {{ customer.customer_type }}
        </v-chip>
        <!--
          Notes counter chip — clicking jumps the scroll container down to
          the Notes section at the bottom of the form. Without this it was
          easy to miss that the customer has notes at all (the section is
          below Change History and below the visible viewport).
        -->
        <v-chip
          v-if="notesCount > 0"
          color="primary"
          size="x-small"
          variant="tonal"
          prepend-icon="mdi-note-text-outline"
          class="ml-2 cursor-pointer"
          title="Jump to notes"
          @click="scrollToNotes"
        >
          Notes ({{ notesCount }})
        </v-chip>
        <v-spacer />
        <v-chip v-if="saving" size="x-small" color="info" variant="tonal">
          <v-icon start size="14">mdi-loading mdi-spin</v-icon>
          Saving...
        </v-chip>
        <v-chip v-else-if="lastSaved" size="x-small" color="success" variant="tonal">
          <v-icon start size="14">mdi-check</v-icon>
          Saved
        </v-chip>
        <v-btn icon="mdi-close" size="small" variant="text" @click="close" class="ml-2" />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-3" style="max-height: 65vh; overflow-y: auto;">
        <!-- Basic Info -->
        <div class="text-subtitle-2 font-weight-bold mb-2">
          <v-icon size="16" class="mr-1">mdi-account</v-icon>
          Basic Information
        </div>
        <v-row dense>
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="form.name"
              label="Customer Name *"
              variant="outlined"
              density="compact"
              @update:focused="(v) => { if (!v) saveField('name') }"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="form.customer_type"
              :items="['Residential', 'Commercial']"
              label="Type"
              variant="outlined"
              density="compact"
              @update:model-value="saveField('customer_type')"
            />
          </v-col>
          <v-col cols="6" sm="4">
            <v-switch
              v-model="form.do_not_mail"
              label="Do Not Mail"
              density="compact"
              hide-details
              color="warning"
              @update:model-value="saveField('do_not_mail')"
            />
          </v-col>
          <v-col cols="6" sm="4">
            <v-switch
              v-model="form.do_not_service"
              label="Do Not Service"
              density="compact"
              hide-details
              color="error"
              @update:model-value="saveField('do_not_service')"
            />
          </v-col>
          <v-col cols="6" sm="4">
            <v-switch
              v-model="form.is_active"
              label="Active"
              density="compact"
              hide-details
              color="success"
              @update:model-value="saveField('is_active')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <PreferredTechnicianField
              v-model="form.preferred_technician_id"
              :current-name="form.preferred_technician_name || ''"
              @update:model-value="onPreferredTechnicianPicked"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <!-- Tags -->
        <div class="text-subtitle-2 font-weight-bold mb-2 d-flex align-center ga-1">
          <v-icon size="16" color="primary">mdi-tag-multiple</v-icon>
          Tags
          <v-chip v-if="form.tag_ids?.length" size="x-small" color="primary" variant="tonal" class="ml-1">
            {{ form.tag_ids.length }}
          </v-chip>
        </div>
        <v-autocomplete
          v-model="form.tag_ids"
          :items="availableTags"
          item-title="name"
          item-value="id"
          hint="System tags (Warranty, Property Management) are used by SMS rules — tagged customers are auto-excluded from SMS journeys."
          persistent-hint
          variant="outlined"
          density="compact"
          multiple
          chips
          closable-chips
          clearable
          class="mb-3"
          :loading="tagsLoading"
          @update:model-value="saveTags"
        >
          <template #chip="{ props: chipProps, item }">
            <v-chip
              v-bind="chipProps"
              :color="item.raw.color || 'primary'"
              size="small"
              variant="tonal"
            >
              <v-icon v-if="item.raw.icon" start size="14">{{ item.raw.icon }}</v-icon>
              {{ item.raw.name }}
              <v-icon v-if="item.raw.is_system" end size="12" color="medium-emphasis" title="System tag">mdi-lock</v-icon>
            </v-chip>
          </template>
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <v-icon :color="item.raw.color || 'primary'" size="18">
                  {{ item.raw.icon || 'mdi-tag' }}
                </v-icon>
              </template>
              <template #title>
                {{ item.raw.name }}
                <v-chip v-if="item.raw.is_system" size="x-small" variant="tonal" color="medium-emphasis" class="ml-1">system</v-chip>
              </template>
              <template #subtitle>
                <span v-if="item.raw.description" class="text-caption">{{ item.raw.description }}</span>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-divider class="my-3" />

        <!-- Contacts -->
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2 font-weight-bold">
            <v-icon size="16" class="mr-1">mdi-phone</v-icon>
            Contacts
          </div>
          <v-spacer />
          <v-btn size="x-small" variant="text" color="accent" @click="addContact">
            <v-icon start size="14">mdi-plus</v-icon>
            Add
          </v-btn>
        </div>
        <div v-if="!form.contacts.length" class="text-body-2 text-medium-emphasis mb-2">
          No contacts added
        </div>
        <v-row v-for="(contact, idx) in form.contacts" :key="'c-' + idx" dense class="mb-1">
          <v-col cols="6" sm="3">
            <v-select
              v-model="contact.contact_type"
              :items="contactTypes"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="6" sm="4">
            <PhoneNumberInput
              v-if="isPhoneType(contact.contact_type)"
              v-model="contact.value"
              label=""
              density="compact"
              hide-details
              :clearable="false"
            />
            <v-text-field
              v-else
              v-model="contact.value"
              placeholder="Value"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="7" sm="3">
            <v-text-field
              v-model="contact.label"
              placeholder="Label (e.g. Manager)"
              variant="outlined"
              density="compact"
              hide-details
              maxlength="100"
            />
          </v-col>
          <v-col cols="4" sm="2" class="d-flex align-center">
            <v-checkbox
              v-model="contact.is_primary"
              label="Primary"
              hide-details
              density="compact"
              color="accent"
            />
          </v-col>
          <v-col cols="1" class="d-flex align-center justify-center">
            <v-btn icon size="x-small" variant="text" color="error" @click="removeContact(idx)">
              <v-icon size="14">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <!-- Locations -->
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2 font-weight-bold">
            <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
            Locations
          </div>
          <v-spacer />
          <v-btn size="x-small" variant="text" color="accent" @click="addLocation">
            <v-icon start size="14">mdi-plus</v-icon>
            Add
          </v-btn>
        </div>
        <div v-if="!form.locations.length" class="text-body-2 text-medium-emphasis mb-2">
          No locations added
        </div>
        <v-card
          v-for="(loc, idx) in form.locations"
          :key="'l-' + idx"
          variant="outlined"
          class="pa-2 mb-2"
        >
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="loc.address_street"
                label="Street"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="loc.address_unit_type"
                :items="unitTypeOptions"
                label="Type"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="loc.address_unit"
                label="Number"
                variant="outlined"
                density="compact"
                hide-details
                placeholder="e.g. 5"
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="loc.address_buzzer"
                label="Buzzer"
                variant="outlined"
                density="compact"
                hide-details
                placeholder="Code"
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="loc.address_city"
                label="City"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="loc.address_state"
                label="Province"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="loc.address_zip"
                label="Postal"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-btn icon size="x-small" variant="text" color="error" @click="removeLocation(idx)">
                <v-icon size="14">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div class="d-flex align-center mt-1">
            <v-checkbox
              v-model="loc.is_primary"
              label="Primary"
              density="compact"
              hide-details
              class="mr-2"
            />
            <v-checkbox
              v-model="loc.is_active"
              label="Active"
              density="compact"
              hide-details
            />
          </div>
        </v-card>

        <v-divider class="my-3" />

        <!-- Change Log -->
        <div class="text-subtitle-2 font-weight-bold mb-2">
          <v-icon size="16" class="mr-1">mdi-history</v-icon>
          Change History
        </div>
        <div v-if="changeLogLoading" class="text-center py-2">
          <v-progress-circular size="20" indeterminate />
        </div>
        <div v-else-if="!changeLog.length" class="text-body-2 text-medium-emphasis mb-2">
          No changes recorded
        </div>
        <div v-else class="change-log-box" style="max-height: 180px; overflow-y: auto;">
          <v-timeline density="compact" side="end" class="change-log-timeline">
            <v-timeline-item
              v-for="entry in changeLog.slice(0, 20)"
              :key="entry.id"
              size="x-small"
              dot-color="primary"
            >
              <div class="text-body-2">
                <span class="font-weight-medium">{{ formatFieldName(entry.field_name) }}</span>
                <template v-if="entry.old_value && entry.new_value">
                  : <span class="text-error text-decoration-line-through">{{ truncate(entry.old_value) }}</span>
                  <v-icon size="12" class="mx-1">mdi-arrow-right</v-icon>
                  <span class="text-success">{{ truncate(entry.new_value) }}</span>
                </template>
                <template v-else-if="entry.new_value">
                  : <span class="text-success">{{ truncate(entry.new_value) }}</span>
                </template>
                <template v-else-if="entry.old_value">
                  : <span class="text-error">removed {{ truncate(entry.old_value) }}</span>
                </template>
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ entry.changed_by_name || 'System' }} &bull; {{ formatDateTime(entry.changed_at) }}
              </div>
            </v-timeline-item>
          </v-timeline>
        </div>

        <v-divider class="my-3" />

        <!--
          Notes — placed at the very bottom (per user request, "B"),
          below Change History. Read-only list with an Add Note button
          that opens a separate dialog (CustomerNoteEditorDialog) for
          create/edit. Always rendered: in saved mode (we have a real
          customerId) the API is hit; the v-if just avoids API noise
          when the dialog re-opens with a stale id of 0/null.

          The `notesAnchorRef` lets the header chip scroll to this block;
          the section also reports its current count back so the chip is
          visible without forcing the user to open the section first.
        -->
        <div ref="notesAnchorRef">
          <CustomerNotesSection
            v-if="props.customerId"
            :customer-id="props.customerId"
            @count-changed="onNotesCountChanged"
          />
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn color="accent" :loading="saving" @click="saveAll">
          <v-icon start size="16">mdi-check</v-icon>
          Save & Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import axios from '@/api/client'
import { useNotify } from '@/composables/useNotify'
import CustomerNotesSection from '@/components/CustomerNotesSection.vue'
import PhoneNumberInput from '@/components/base/PhoneNumberInput.vue'
import PreferredTechnicianField from '@/modules/crm/customer-card/components/sidebar/PreferredTechnicianField.vue'

const { notify } = useNotify()

const props = defineProps({
  modelValue: Boolean,
  customerId: [Number, String],
})

const emit = defineEmits(['update:modelValue', 'updated'])

const dialogVisible = ref(false)
const customer = ref(null)
const form = ref({ contacts: [], locations: [], tag_ids: [] })
const changeLog = ref([])
const changeLogLoading = ref(false)
const saving = ref(false)

// Notes counter (header chip) and scroll anchor for `scrollToNotes()`.
const notesCount = ref(0)
const notesAnchorRef = ref(null)
function onNotesCountChanged(n) {
  notesCount.value = Number(n) || 0
}
function scrollToNotes() {
  setTimeout(() => {
    notesAnchorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, 50)
}

const availableTags = ref([])
const tagsLoading = ref(false)

const unitTypeOptions = ['Unit', 'Apartment', 'Suite', 'Floor', 'Room', 'Basement', 'Townhouse', 'PO Box']
const lastSaved = ref(false)
const originalValues = ref({})

const contactTypes = ['Phone', 'MobilePhone', 'Email']
function isPhoneType(t) {
  return t === 'Phone' || t === 'MobilePhone' || t === 'Fax'
}

watch(() => props.modelValue, (val) => {
  dialogVisible.value = val
  if (val && props.customerId) {
    loadCustomer()
    loadChangeLog()
    loadAvailableTags()
  }
})

watch(dialogVisible, (val) => {
  if (!val) emit('update:modelValue', false)
})

async function loadCustomer() {
  try {
    const response = await axios.get(`/jobs/customers/${props.customerId}/st-detail/`)
    customer.value = response.data
    const data = response.data
    const loadedTagIds = Array.isArray(data.tag_ids)
      ? [...data.tag_ids]
      : Array.isArray(data.tags)
        ? data.tags.map(t => t.id).filter(Boolean)
        : []
    form.value = {
      name: data.name || '',
      customer_type: data.customer_type || 'Residential',
      notes: data.notes || '',
      do_not_mail: data.do_not_mail || false,
      do_not_service: data.do_not_service || false,
      is_active: data.is_active !== false,
      preferred_technician_id: data.preferred_technician_id ?? null,
      preferred_technician_name: data.preferred_technician_name || '',
      tag_ids: loadedTagIds,
      contacts: (data.contacts || []).map(c => ({ ...c, is_primary: c.is_primary || false })),
      locations: (data.locations || []).map(l => ({
        ...l,
        is_active: l.is_active !== false,
        is_primary: l.is_primary || false,
      })),
    }
    console.log('[CustomerEdit] Loaded tag_ids:', loadedTagIds)
    originalValues.value = {
      name: form.value.name,
      customer_type: form.value.customer_type,
      notes: form.value.notes,
      do_not_mail: form.value.do_not_mail,
      do_not_service: form.value.do_not_service,
      is_active: form.value.is_active,
      preferred_technician_id: form.value.preferred_technician_id,
    }
    console.log('[CustomerEdit] Loaded customer:', data.id, data.name)
  } catch (err) {
    console.error('[CustomerEdit] Failed to load customer:', err)
    notify.error('Failed to load customer')
  }
}

async function loadAvailableTags() {
  if (availableTags.value.length) return
  tagsLoading.value = true
  try {
    const response = await axios.get('/jobs/settings/tags/', { params: { is_active: true } })
    const tags = response.data?.results || response.data || []
    availableTags.value = (tags || []).filter(t => t.is_active !== false)
    console.log('[CustomerEdit] Loaded', availableTags.value.length, 'available tags')
  } catch (err) {
    console.warn('[CustomerEdit] Failed to load tags:', err)
  } finally {
    tagsLoading.value = false
  }
}

async function saveTags() {
  if (!props.customerId) return
  try {
    console.log('[CustomerEdit] Saving tag_ids:', form.value.tag_ids)
    await axios.put(`/jobs/customers/${props.customerId}/tags/`, {
      tag_ids: form.value.tag_ids || [],
    })
    notify.success('Tags updated')
    lastSaved.value = true
    setTimeout(() => { lastSaved.value = false }, 2000)
    await loadChangeLog()
  } catch (err) {
    console.error('[CustomerEdit] Failed to save tags:', err)
    notify.error('Failed to save tags: ' + (err.response?.data?.detail || err.message))
  }
}

async function loadChangeLog() {
  changeLogLoading.value = true
  try {
    const response = await axios.get(`/jobs/customers/${props.customerId}/change-log/`)
    changeLog.value = response.data || []
    console.log('[CustomerEdit] Loaded change log:', changeLog.value.length)
  } catch (err) {
    console.error('[CustomerEdit] Failed to load change log:', err)
    changeLog.value = []
  } finally {
    changeLogLoading.value = false
  }
}

function onPreferredTechnicianPicked() {
  saveField('preferred_technician_id')
}

async function saveField(fieldName) {
  if (!customer.value) return
  const currentVal = String(form.value[fieldName] ?? '')
  const origVal = String(originalValues.value[fieldName] ?? '')
  if (currentVal === origVal) {
    console.log('[CustomerEdit] No change for', fieldName, '- skip save')
    return
  }
  console.log('[CustomerEdit] Saving field:', fieldName, 'old:', origVal, 'new:', currentVal)
  const payload = { [fieldName]: form.value[fieldName] }
  const ok = await doSave(payload)
  if (ok) {
    originalValues.value[fieldName] = form.value[fieldName]
    await loadChangeLog()
  }
}

// Block malformed / non-Latin emails before save (mirrors the backend gate).
const EMAIL_SHAPE = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/
function validateEmailContacts() {
  for (const c of (form.value.contacts || [])) {
    if (c.contact_type !== 'Email') continue
    const raw = (c.value || '').replace(/\u00a0/g, ' ').trim()
    if (!raw) continue
    const bad = [...raw].filter(ch => ch.charCodeAt(0) > 127)
    if (bad.length) {
      const list = [...new Set(bad)]
        .map(ch => `'${ch}' (U+${ch.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')})`)
        .join(', ')
      notify.error(`Email "${raw}" contains invalid non-Latin character(s): ${list}. `
        + 'Re-type it using standard Latin letters.')
      return false
    }
    if (!EMAIL_SHAPE.test(raw)) {
      notify.error(`"${raw}" is not a valid email address.`)
      return false
    }
  }
  return true
}

async function saveAll() {
  if (!customer.value) return
  if (!validateEmailContacts()) return
  const payload = {
    name: form.value.name,
    customer_type: form.value.customer_type,
    notes: form.value.notes,
    do_not_mail: form.value.do_not_mail,
    do_not_service: form.value.do_not_service,
    is_active: form.value.is_active,
    preferred_technician_id: form.value.preferred_technician_id ?? null,
    contacts: form.value.contacts,
    locations: form.value.locations,
  }
  const success = await doSave(payload)
  if (success) {
    await loadChangeLog()
    emit('updated', customer.value)
    close()
  }
}

async function doSave(payload) {
  saving.value = true
  lastSaved.value = false
  try {
    const response = await axios.patch(
      `/jobs/customers/${props.customerId}/update-with-log/`,
      payload
    )
    customer.value = response.data
    saving.value = false
    lastSaved.value = true
    setTimeout(() => { lastSaved.value = false }, 3000)
    notify.success('Customer saved')
    console.log('[CustomerEdit] Saved successfully')
    return true
  } catch (err) {
    console.error('[CustomerEdit] Save failed:', err)
    const d = err.response?.data
    const msg = typeof d === 'string' ? d
      : (d?.detail || d?.value || (Array.isArray(d?.value) ? d.value[0] : null)
         || err.message)
    notify.error('Failed to save customer: ' + msg)
    saving.value = false
    return false
  }
}

function addContact() {
  form.value.contacts.push({
    contact_type: 'Phone',
    value: '',
    label: '',
    is_primary: form.value.contacts.length === 0,
  })
}

function removeContact(idx) {
  form.value.contacts.splice(idx, 1)
}

function addLocation() {
  form.value.locations.push({
    address_street: '',
    address_unit_type: 'Unit',
    address_unit: '',
    address_buzzer: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: 'Canada',
    is_primary: form.value.locations.length === 0,
    is_active: true,
  })
}

function removeLocation(idx) {
  form.value.locations.splice(idx, 1)
}

function close() {
  dialogVisible.value = false
}

function formatFieldName(name) {
  if (!name) return ''
  return name
    .replace(/^contact\.\d+\./, 'Contact ')
    .replace(/^location\.\d+\./, 'Location ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}

function formatDateTime(dt) {
  if (!dt) return ''
  try {
    return new Date(dt).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return dt }
}

function truncate(val, max = 60) {
  if (!val) return ''
  return val.length > max ? val.slice(0, max) + '...' : val
}
</script>

<style scoped>
.change-log-timeline :deep(.v-timeline-item__body) {
  padding-top: 2px;
  padding-bottom: 8px;
}
.change-log-box {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 4px;
  padding: 4px 0;
}
</style>
